import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import {
  Layers, Loader2, ShieldCheck, Gauge, Radio, Percent, Activity, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

const confColor = (v) => (v == null ? "#64748b" : v >= 75 ? "#16a34a" : v >= 45 ? "#b45309" : "#ef4444");
const varColor = { bassa: "#16a34a", low: "#16a34a", media: "#b45309", medium: "#b45309", alta: "#ef4444", high: "#ef4444" };

function ConfGauge({ value, level, L }) {
  const r = 46, c = 2 * Math.PI * r, off = c * (1 - (value || 0) / 100), col = confColor(value);
  return (
    <div className="relative w-[116px] h-[116px] shrink-0" data-testid="pi-conf-gauge">
      <svg width="116" height="116" className="-rotate-90">
        <circle cx="58" cy="58" r={r} fill="none" stroke="currentColor" strokeWidth="9" className="text-muted/30" />
        <circle cx="58" cy="58" r={r} fill="none" stroke={col} strokeWidth="9" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-data text-2xl font-bold" style={{ color: col }} data-testid="pi-confidence">{value}%</span>
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground">{level}</span>
      </div>
    </div>
  );
}

const WEIGHT_LABEL = {
  property_history: ["Storico struttura", "Property history"],
  market_benchmark: ["Mercato live", "Live market"],
  users_benchmark: ["Benchmark utenti", "Users benchmark"],
};
const WEIGHT_UNIT = {
  property_history: { it: ["prenotazione", "prenotazioni"], en: ["booking", "bookings"] },
  market_benchmark: { it: ["competitor monitorato", "competitor monitorati"], en: ["monitored competitor", "monitored competitors"] },
  users_benchmark: { it: ["struttura simile", "strutture simili"], en: ["similar structure", "similar structures"] },
};

export default function PricingIntelligence({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/pricing-intelligence?lang=${lang}`); setD(data); }
    catch { setD(null); } finally { setLoading(false); }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load]);

  const conf = d?.confidence || {};
  const w = d?.source_weights || {};
  const ch = d?.channel_normalization || {};
  const mp = d?.market_position || {};
  const stab = d?.rate_stability || {};
  const WCOL = { property_history: "hsl(var(--primary))", market_benchmark: "#0ea5e9", users_benchmark: "#8b5cf6" };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="mp-pricing-intelligence">
      <div className="flex items-center gap-2 mb-1"><Layers className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">Pricing Intelligence</h2></div>
      <p className="text-sm text-muted-foreground mb-5">{L("Il prezzo di mercato (Booking) misura solo il posizionamento; il prezzo realmente venduto (PMS/file) personalizza il consiglio. I due dataset non vengono mai confrontati direttamente.", "The market price (Booking) only measures positioning; the actually-sold price (PMS/file) personalises the advice. The two datasets are never compared directly.")}</p>

      {loading ? (
        <div className="py-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /></div>
      ) : !d ? <p className="text-sm text-muted-foreground">—</p> : (
        <div className="space-y-6">
          {/* confidence + source weights */}
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <ConfGauge value={conf.score} level={conf.level} L={L} />
            <div className="flex-1 w-full">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />{L("Affidabilità del suggerimento", "Suggestion confidence")}</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {(conf.drivers || []).map((dr, i) => (
                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary" style={{ opacity: 0.5 + dr.score / 200 }}>{dr.label} {dr.score}%</span>
                ))}
              </div>
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Peso delle fonti", "Source weights")}</p>
              <div className="h-3 rounded-full overflow-hidden flex" data-testid="pi-source-weights">
                {["property_history", "market_benchmark", "users_benchmark"].map((k) => (
                  <div key={k} style={{ width: `${w[k] || 0}%`, backgroundColor: WCOL[k] }} title={`${WEIGHT_LABEL[k][lang === "en" ? 1 : 0]} ${w[k] || 0}%`} />
                ))}
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                {["property_history", "users_benchmark", "market_benchmark"].map((k) => {
                  const n = (d.source_weights_counts || {})[k] || 0;
                  const unit = WEIGHT_UNIT[k][lang === "en" ? "en" : "it"][n === 1 ? 0 : 1];
                  return (
                  <span key={k} className="text-[11px] inline-flex items-center gap-1.5" data-testid={`pi-weight-${k}`}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: WCOL[k] }} />
                    {WEIGHT_LABEL[k][lang === "en" ? 1 : 0]}: <b className="font-data">{w[k] || 0}%</b>
                    <span className="text-muted-foreground">({n} {unit})</span>
                  </span>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">{L("I pesi sono dinamici: cresceranno automaticamente man mano che raccogli più storico e aumentano le strutture nel benchmark anonimo.", "Weights are dynamic: they grow automatically as you collect more history and more structures join the anonymous benchmark.")}</p>
            </div>
          </div>

          {/* market position index */}
          {mp.available && (
            <div data-testid="pi-market-position">
              <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3 inline-flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" />Market Position Index</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3 text-sm">
                <span>{L("Mercato osservato", "Observed market")}: <b className="font-data">€{mp.market_avg}</b></span>
                <span>{L("Realmente venduto", "Actually sold")}: <b className="font-data">€{mp.realized_avg}</b></span>
                <span className="inline-flex items-center gap-1.5">Position: <b className="font-data text-lg" style={{ color: mp.overall_index >= 100 ? "#16a34a" : "#b45309" }} data-testid="pi-position-index">{mp.overall_index}%</b></span>
              </div>
              {mp.history?.length > 1 && (() => {
                const hs = mp.history, idxs = hs.map((h) => h.index);
                const lo = Math.min(...idxs, 100), hi = Math.max(...idxs, 100), span = Math.max(1, hi - lo);
                const W = 320, H = 70, step = W / (hs.length - 1);
                const pts = hs.map((h, i) => `${i * step},${H - ((h.index - lo) / span) * H}`).join(" ");
                const zeroY = H - ((100 - lo) / span) * H;
                const dev = mp.avg_last6_index != null ? mp.avg_last6_index - 100 : null;
                return (
                  <div className="mb-4" data-testid="pi-position-history">
                    <p className="text-[11px] font-bold text-muted-foreground mb-2">{L("Storico posizionamento vs mercato", "Positioning history vs market")}</p>
                    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[420px] h-[70px]" preserveAspectRatio="none">
                      <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" className="text-muted-foreground/40" />
                      <polyline fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" points={pts} vectorEffect="non-scaling-stroke" />
                      {hs.map((h, i) => <circle key={i} cx={i * step} cy={H - ((h.index - lo) / span) * H} r="3" fill="hsl(var(--primary))" />)}
                    </svg>
                    <div className="flex justify-between text-[10px] text-muted-foreground mt-1 max-w-[420px]">
                      <span>{hs[0].month}</span><span>{hs[hs.length - 1].month}</span>
                    </div>
                    {dev != null && (
                      <p className="text-sm mt-2" data-testid="pi-position-summary">
                        {L(`Negli ultimi ${Math.min(6, hs.length)} mesi la tua struttura si è posizionata mediamente il `, `Over the last ${Math.min(6, hs.length)} months your property positioned on average `)}
                        <b style={{ color: dev >= 0 ? "#16a34a" : "#b45309" }}>{Math.abs(dev)}% {dev >= 0 ? L("sopra", "above") : L("sotto", "below")}</b>
                        {L(" il mercato.", " the market.")}
                      </p>
                    )}
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[["by_channel", L("Per canale", "By channel")], ["by_season", L("Per stagione", "By season")], ["by_weekday", L("Per giorno", "By weekday")], ["by_room_type", L("Per tipologia", "By room type")]].map(([key, label]) => (
                  (mp[key] || []).length > 0 && (
                    <div key={key}>
                      <p className="text-[11px] font-bold text-muted-foreground mb-1.5">{label}</p>
                      <div className="space-y-1">
                        {mp[key].map((x, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="capitalize">{x.key}</span>
                            <span className="font-data" style={{ color: x.index >= 100 ? "#16a34a" : "#b45309" }}>{x.index}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* channel normalization */}
            {ch.available && (
              <div data-testid="pi-channels">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3 inline-flex items-center gap-1.5"><Radio className="w-3.5 h-3.5" />{L("Normalizzazione canali (ADR)", "Channel normalization (ADR)")}</p>
                <div className="space-y-1.5">
                  {ch.channels.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm rounded-md bg-muted/40 px-3 py-2">
                      <span>{c.channel} <span className="text-[11px] text-muted-foreground">· {c.bookings}</span></span>
                      <span className="inline-flex items-center gap-2">
                        <b className="font-data">€{c.adr}</b>
                        <span className="inline-flex items-center text-[11px]" style={{ color: c.delta_pct >= 0 ? "#16a34a" : "#ef4444" }}>
                          {c.delta_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{c.delta_pct > 0 ? "+" : ""}{c.delta_pct}%
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* rate stability / price discipline */}
            {stab.available && (
              <div data-testid="pi-rate-stability">
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3 inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" />{L("Disciplina tariffaria", "Price discipline")}</p>
                <div className="rounded-lg bg-muted/40 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">{L("Stabilità tariffaria", "Rate stability")}</span>
                    <span className="font-data text-xl font-bold" data-testid="pi-stability-score">{stab.score}/100</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden mb-2"><div className="h-full rounded-full" style={{ width: `${stab.score}%`, backgroundColor: confColor(stab.score) }} /></div>
                  <div className="flex flex-wrap items-center gap-x-4 text-[11px] text-muted-foreground">
                    <span>{L("Variabilità", "Variability")}: <b style={{ color: varColor[stab.variability] }}>{stab.variability}</b></span>
                    <span>{L("Prezzo medio", "Mean price")}: <b className="text-foreground font-data">€{stab.mean_price}</b> ±€{stab.std_price}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">{L("Uno storico stabile viene considerato più affidabile e pesa di più nel suggerimento.", "A stable history is considered more reliable and weighs more in the suggestion.")}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
